import { Session } from '@supabase/supabase-js';

export interface ContainerTrackingEvent {
  event: string;
  location: string;
  timestamp: string;
  vessel?: string;
}

export interface ContainerTrackingData {
  container_number: string;
  carrier: {
    name: string;
    code: string;
  };
  bol?: string;
  origin?: {
    port_name: string;
    port_code: string;
  };
  destination?: {
    port_name: string;
    port_code: string;
  };
  vessel?: {
    name: string;
    voyage: string;
  };
  etd?: string;
  eta?: string;
  ata?: string;
  events?: ContainerTrackingEvent[];
}

export interface TrackContainerResponse {
  success: boolean;
  container_number: string;
  carrier_code?: string;
  tracking_data?: {
    success: boolean;
    data?: ContainerTrackingData;
    error?: string;
    message?: string;
  };
  error?: string;
}

export const trackContainer = async (
  session: Session,
  containerNumber: string,
  carrierCode?: string
): Promise<TrackContainerResponse> => {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track-container`;

    const payload: any = {
      container_number: containerNumber,
    };

    if (carrierCode) {
      payload.carrier_code = carrierCode;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error tracking container:', error);
    return {
      success: false,
      container_number: containerNumber,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

// Helper function to get carrier code from carrier name
export const getCarrierCode = (carrierName?: string): string | undefined => {
  if (!carrierName) return undefined;

  const carrierMap: Record<string, string> = {
    maersk: 'MAEU',
    msc: 'MSCU',
    'cma cgm': 'CMDU',
    cosco: 'COSU',
    'hapag-lloyd': 'HLCU',
    'hapag lloyd': 'HLCU',
    one: 'ONEY',
    evergreen: 'EGLV',
    'yang ming': 'YMLU',
    hyundai: 'HDMU',
    zim: 'ZIMU',
    'wan hai': 'WHLC',
    pil: 'PCIU',
    'sm line': 'SMLU',
  };

  const normalized = carrierName.toLowerCase().trim();
  return carrierMap[normalized];
};

export interface SyncContainerResponse {
  success: boolean;
  container_number: string;
  sync_results?: {
    market_created: boolean;
    ship_created: boolean;
    ship_trip_created: boolean;
    ship_trip_updated?: boolean;
    settlement_updated: boolean;
  };
  tracking_data?: ContainerTrackingData;
  created_ids?: {
    market_id: string | null;
    ship_id: number | null;
    ship_trip_id: number | null;
  };
  error?: string;
}

/**
 * Tracks a container and syncs the data to database (creates/updates ships, ship_trips, markets)
 * @param session - User session
 * @param containerNumber - Container number to track
 * @param settlementId - Optional settlement ID to link the ship trip to
 * @param carrierCode - Optional carrier code to speed up search
 */
export const syncContainerTracking = async (
  session: Session,
  containerNumber: string,
  settlementId?: number,
  carrierCode?: string
): Promise<SyncContainerResponse> => {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-container-tracking`;

    const payload: any = {
      container_number: containerNumber,
    };

    if (settlementId) {
      payload.settlement_id = settlementId;
    }

    if (carrierCode) {
      payload.carrier_code = carrierCode;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error syncing container tracking:', error);
    return {
      success: false,
      container_number: containerNumber,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export interface BatchTrackContainersPayload {
  season?: string;
  exporterId?: number;
  exporterIds?: number[];
  shipName?: string;
  maxGroups?: number;
  dryRun?: boolean;
  includeDetails?: boolean;
  throttleMs?: number;
  preview?: boolean;
  groupKeys?: string[];
  force?: boolean;
  manualShipTripId?: number;
  shareApiAcrossExporters?: boolean;
}

export interface BatchTrackStats {
  shipment_boxes_considered: number;
  containers_considered: number;
  containers_already_tracked: number;
  groups_total: number;
  groups_processed: number;
  groups_reused_existing_trip: number;
  groups_failed: number;
  groups_dry_run: number;
  api_calls: number;
  containers_tracked: number;
}

export interface BatchTrackDetail {
  exporter_id: number;
  key: string;
  source_ship_name: string | null;
  source_ship_etd: string | null;
  container_numbers: string[];
  status:
    | 'synced'
    | 'skipped'
    | 'reused'
    | 'error'
    | 'dry-run'
    | 'pending'
    | 'manual';
  message?: string;
  ship_trip_id?: number | null;
}

export interface BatchTrackPreviewGroup {
  key: string;
  exporter_id: number;
  season: string;
  source_ship_name: string | null;
  source_ship_etd: string | null;
  source_ship_eta: string | null;
  ship_origin_port?: string | null;
  ship_origin_country?: string | null;
  ship_destination_port?: string | null;
  country?: string | null;
  container_numbers: string[];
  containers_count: number;
  will_reuse_existing_trip: boolean;
  estimated_api_call: boolean;
}

export interface BatchTrackContainersResponse {
  success: boolean;
  run_id?: string;
  season?: string;
  stats?: BatchTrackStats;
  errors?: string[];
  details?: BatchTrackDetail[];
  preview?: boolean;
  preview_groups?: BatchTrackPreviewGroup[];
  error?: string;
}

export const batchTrackShipmentContainers = async (
  session: Session,
  payload: BatchTrackContainersPayload
): Promise<BatchTrackContainersResponse> => {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/batch-track-containers`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        season: payload.season,
        exporter_id: payload.exporterId,
        exporter_ids: payload.exporterIds,
        ship_name: payload.shipName,
        max_groups: payload.maxGroups,
        dry_run: payload.dryRun,
        include_details: payload.includeDetails,
        throttle_ms: payload.throttleMs,
        preview: payload.preview,
        group_keys: payload.groupKeys,
        force: payload.force,
        manual_ship_trip_id: payload.manualShipTripId,
        share_api_across_exporters: payload.shareApiAcrossExporters,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error running batch tracking:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

export interface TrackingSummaryRow {
  exporter_id: number;
  exporter_name: string;
  unique_containers: number;
  containers_tracked: number;
}

export interface TrackingSummaryResponse {
  success: boolean;
  season: string;
  species_id: number;
  totals: {
    unique_containers: number;
    containers_tracked: number;
  };
  rows: TrackingSummaryRow[];
  error?: string;
}

export const fetchTrackingSummary = async (
  session: Session,
  payload: { season: string; speciesId: number; exporterId?: number }
): Promise<TrackingSummaryResponse> => {
  try {
    if (!session?.access_token) {
      throw new Error('No authentication token available');
    }

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/tracking-summary`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        season: payload.season,
        species_id: payload.speciesId,
        exporter_id: payload.exporterId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Error ${response.status}: ${errorText}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error fetching tracking summary:', error);
    return {
      success: false,
      season: payload.season,
      species_id: payload.speciesId,
      totals: { unique_containers: 0, containers_tracked: 0 },
      rows: [],
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};
